import { useEffect, Suspense } from "react";
import { useAuthStore } from "@/store/useAuthStore";
import { useNavigate, useLocation, useSearchParams } from "react-router";
import { UserRole } from "@/constants";
import GTMService from "@/services/GTMService";
import Cookie from "js-cookie";
import { safeSessionGetItem, safeSessionSetItem } from "@/utils/safeStorage";

function AuthProviderContent({ children }: { children: React.ReactNode }) {
  const isLoggedIn = useAuthStore((state) => state.isLoggedIn);
  const setToken = useAuthStore((state) => state.setToken);
  const logoutUser = useAuthStore((state) => state.logoutUser);
  // const token = useAuthStore((state) => state.token);
  const organizationId = useAuthStore((state) => state.organizationId);
  const role = useAuthStore((state) => state.role);
  const initCompleted = useAuthStore((state) => state.initCompleted);

  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();

  useEffect(() => {
    const storeToken = (token: string) => {
      Cookie.set("accessToken", token, { expires: 0.25, path: "/" }); // Set cookie with 6 hours expiry
    };
    const accessToken = searchParams.get("token");
    if (accessToken) {
      setToken(accessToken);
      safeSessionSetItem("accessToken", accessToken);
      storeToken(accessToken);
    } else if (safeSessionGetItem("accessToken")) {
      const storedToken = safeSessionGetItem("accessToken");
      if (storedToken) {
        setToken(storedToken);
        storeToken(storedToken);
      }
    } else {
      logoutUser().catch(console.error);
    }
  }, [setToken, logoutUser, navigate, location.pathname, searchParams]);

  useEffect(() => {
    if (initCompleted) {
      // Skip authentication check for auth flow pages
      if (
        location.pathname.startsWith("/auth/clover/app-selection") ||
        location.pathname === "/auth/error"
      ) {
        return;
      }

      if (!isLoggedIn()) {
        navigate("/login");
      } else if (!organizationId && role === UserRole.SUPERADMIN) {
        navigate("/admin/switch");
        GTMService.loginEvent({ organizationId, role });
      } else {
        GTMService.loginEvent({ organizationId, role });
      }
    }
  }, [
    isLoggedIn,
    navigate,
    organizationId,
    role,
    initCompleted,
    location.pathname
  ]);

  return <>{children}</>;
}

const AuthProvider = ({ children }: { children: React.ReactNode }) => {
  return (
    <Suspense fallback={<div>Loading...</div>}>
      <AuthProviderContent>{children}</AuthProviderContent>
    </Suspense>
  );
};

export default AuthProvider;
